import React, { useCallback, useState } from "react";
import { get } from "lodash";
import { DraggableBlockOption } from "shared/components/BlockOption/DraggableBlockOption";
import { BlockNestedKey, Blockable, getParentPath } from "../../helpers/blockTree";
import { BlockToEdit } from "../../hooks/useMainSurvey";
import { BlockTypes } from "../../helpers/blocks";

export const BlockTreeBlock = (props: BlockTreeBlockProps) => {
  const {
    index,
    treePath,
    tree,
    blockToEdit,
    onDrop,
    onDragStart,
    setDragOverParentPath,
    dragOverParentPath,
    isNotDraggable,
    isHidden,
    handleOnClick,
    handleOnExpand,
    draggedBlockPath,
    selectedBlocks,
  } = props;

  const [isCursorOver, setIsCursorOver] = useState<boolean>(false);
  const [isShiftPressed, setIsShiftPressed] = useState<boolean>(false);

  const blockable = (get(tree, treePath) || tree) as Blockable;
  const nestedCount = treePath.split(BlockNestedKey).length - 1;
  const isHasNested = [BlockTypes.Survey, BlockTypes.MainSurvey].includes(blockable.blockType);
  const parentPath = isHasNested ? treePath : getParentPath(treePath);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();

      if (e.shiftKey && !isShiftPressed) {
        setIsShiftPressed(true);
      } else if (!e.shiftKey && isShiftPressed) {
        setIsShiftPressed(false);
      }

      if (!isCursorOver) {
        setIsCursorOver(true);
        setDragOverParentPath(parentPath);
      }
    },
    [isShiftPressed, isCursorOver, parentPath, setDragOverParentPath]
  );

  const handleDragLeave = useCallback(() => {
    setIsCursorOver(false);
    setDragOverParentPath(null);
  }, [setDragOverParentPath]);

  const handleOnDrop = useCallback(
    (e: React.DragEvent) => {
      handleDragLeave();
      if (draggedBlockPath === treePath || (!isShiftPressed && blockable.blockType === BlockTypes.Chart)) {
        return;
      }

      onDrop(e, isShiftPressed ? getParentPath(treePath) : parentPath, isHasNested && !isShiftPressed ? 0 : index + 1);
    },
    [
      handleDragLeave,
      draggedBlockPath,
      treePath,
      isShiftPressed,
      blockable.blockType,
      onDrop,
      parentPath,
      isHasNested,
      index,
    ]
  );

  if (!blockable) {
    return null;
  }

  return (
    <>
      <DraggableBlockOption
        onDragStart={!isNotDraggable ? (e) => onDragStart(e, treePath) : undefined}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleOnDrop}
        onClick={(e) => handleOnClick(e, treePath)}
        blockType={blockable.blockType}
        title={blockable.title}
        nestedCount={nestedCount}
        handleExpand={
          blockable.blockType === BlockTypes.Survey ? (isExpanded) => handleOnExpand(treePath, isExpanded) : undefined
        }
        isExpanded={blockable.isExpanded}
        isCursorOver={isCursorOver}
        isDraggedOverNested={dragOverParentPath === treePath}
        isNotDraggable={isNotDraggable}
        isHidden={isHidden}
        isSelected={!treePath ? !blockToEdit.id : blockToEdit.id === blockable.id}
        isMultiSelected={selectedBlocks.some((b) => b.id === blockable.id)}
        isLoading={blockable.isGettingNested}
      />

      {blockable[BlockNestedKey]?.map((_block, i) => (
        <BlockTreeBlock
          key={i}
          index={i}
          tree={tree}
          treePath={`${treePath}["${[BlockNestedKey]}"][${i}]`}
          blockToEdit={blockToEdit}
          onDrop={onDrop}
          onDragStart={onDragStart}
          setDragOverParentPath={setDragOverParentPath}
          dragOverParentPath={dragOverParentPath}
          isHidden={!!isHidden || !blockable.isExpanded}
          handleOnClick={handleOnClick}
          handleOnExpand={handleOnExpand}
          draggedBlockPath={draggedBlockPath}
          selectedBlocks={selectedBlocks}
        />
      ))}
    </>
  );
};

interface BlockTreeBlockProps {
  index: number;
  treePath: string;
  tree: Blockable;
  blockToEdit: BlockToEdit;
  onDrop: (e: React.DragEvent, parentPath: string, toIndex: number) => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, blockPath: string, blockPosition?: string | null) => void;
  setDragOverParentPath: (parentPath: string | null) => void;
  dragOverParentPath: string | null;
  isNotDraggable?: boolean;
  isHidden?: boolean;
  handleOnClick: (e: React.MouseEvent, blockPath: string) => void;
  handleOnExpand: (blockPath: string, isExpanded: boolean) => void;
  draggedBlockPath: string | null;
  selectedBlocks: Blockable[];
}
